import React, { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { paths } from './paths';
import { RequireAuth, RequireRole } from './guards';
import { ROLES, ORGANISER_ROLES, STAFF_MODE_ROLES } from '../auth/roles';
import StaffShell from '../pages/staff/StaffShell';
import PublicLayout from '../layouts/PublicLayout';
import AuthLayout from '../layouts/AuthLayout';
import AdminLayout from '../layouts/AdminLayout';
import NotFound from '../pages/NotFound';
import OrganiserLayout from '../layouts/OrganiserLayout';
import { sectionRoutes } from './sectionRoutes';
import { ADMIN_SECTIONS, MyProfile } from '../pages/admin/sections';
import { ORGANISER_SECTIONS } from '../pages/organiser/sections';

// Every page is loaded on demand (code splitting), so the first paint only ships what it needs.
const Home = lazy(() => import('../pages/Home'));
const EventDetail = lazy(() => import('../pages/EventDetail'));
const SelectTickets = lazy(() => import('../pages/event/SelectTickets'));
const Checkout = lazy(() => import('../pages/event/Checkout'));
const Confirmation = lazy(() => import('../pages/event/Confirmation'));
const TicketView = lazy(() => import('../pages/event/TicketView'));
const MyTickets = lazy(() => import('../pages/orders/MyTickets'));
const Orders = lazy(() => import('../pages/orders/Orders'));
const OrderDetail = lazy(() => import('../pages/orders/OrderDetail'));
const OrderTickets = lazy(() => import('../pages/orders/OrderTickets'));
const RefundRequest = lazy(() => import('../pages/orders/RefundRequest'));
const RefundStatus = lazy(() => import('../pages/orders/RefundStatus'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const DataRights = lazy(() => import('../pages/DataRights'));
const CmsPage = lazy(() => import('../pages/CmsPage'));
const PaymentMethods = lazy(() => import('../pages/PaymentMethods'));
const Nearby = lazy(() => import('../pages/Nearby'));
const Notifications = lazy(() => import('../pages/Notifications'));
const NotificationPreferences = lazy(() => import('../pages/NotificationPreferences'));
const Login = lazy(() => import('../pages/Login'));
const Register = lazy(() => import('../pages/Register'));
const RegisterVerify = lazy(() => import('../pages/RegisterVerify'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const OrganiserLogin = lazy(() => import('../pages/OrganiserLogin'));
const OrganiserApply = lazy(() => import('../pages/OrganiserApply'));
const StaffIndex = lazy(() => import('../pages/staff/StaffIndex'));
const StaffHome = lazy(() => import('../pages/staff/StaffHome'));
const StaffScan = lazy(() => import('../pages/staff/StaffScan'));
const StaffScanResult = lazy(() => import('../pages/staff/StaffScanResult'));
const StaffManual = lazy(() => import('../pages/staff/StaffManual'));
const StaffAttendee = lazy(() => import('../pages/staff/StaffAttendee'));

/**
 * The whole route tree, in one place. Three layouts decide the page chrome and guards decide who
 * gets in:
 *   PublicLayout  site header + footer          (browse, dashboards)
 *   AuthLayout    bare full-screen pages         (login, register, password screens)
 *   AdminLayout / OrganiserLayout   sidebar + top bar   (/admin/*, /organizer-dashboard/*; one shared shell)
 * To add a page: add its path to routes/paths.js, then one <Route> below inside the right layout
 * and guard. Anything unmatched falls through to the 404 page.
 */
export default function AppRoutes() {
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path={paths.home} element={<Home />} />
        <Route path={paths.event()} element={<EventDetail />} />
        <Route path={paths.eventTickets()} element={<SelectTickets />} />
        <Route path={paths.eventCheckout()} element={<Checkout />} />
        <Route path={paths.eventConfirmed()} element={<Confirmation />} />
        <Route path={paths.eventTicket()} element={<TicketView />} />
        <Route path={paths.nearby} element={<Nearby />} />
        <Route path={paths.page()} element={<CmsPage />} />
        <Route path={paths.myTickets} element={<MyTickets />} />

        <Route element={<RequireAuth />}>
          <Route path={paths.attendeeDashboard} element={<Dashboard />} />
          <Route path={paths.notifications} element={<Notifications />} />
          <Route path={paths.dataRights} element={<DataRights />} />
          <Route path={paths.paymentMethods} element={<PaymentMethods />} />
          <Route path={paths.notificationPreferences} element={<NotificationPreferences />} />
          <Route path={paths.orders} element={<Orders />} />
          <Route path={paths.order()} element={<OrderDetail />} />
          <Route path={paths.orderTickets()} element={<OrderTickets />} />
          <Route path={paths.orderRefund()} element={<RefundRequest />} />
          <Route path={paths.orderRefundStatus()} element={<RefundStatus />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>

      <Route element={<AuthLayout />}>
        <Route path={paths.login} element={<Login />} />
        <Route path={paths.register} element={<Register />} />
        <Route path={paths.registerVerify} element={<RegisterVerify />} />
        <Route path={paths.forgotPassword} element={<ForgotPassword loginPath={paths.login} />} />
        <Route path={paths.resetPassword} element={<ResetPassword loginPath={paths.login} />} />
        <Route path={paths.organiser.login} element={<OrganiserLogin />} />
        <Route path={paths.organiser.apply} element={<OrganiserApply />} />
        <Route path={paths.organiser.forgotPassword} element={<ForgotPassword loginPath={paths.organiser.login} />} />
        <Route path={paths.organiser.resetPassword} element={<ResetPassword loginPath={paths.organiser.login} />} />
      </Route>

      <Route element={<RequireRole roles={STAFF_MODE_ROLES} />}>
        <Route path={paths.staff.root} element={<StaffShell />}>
          <Route index element={<StaffIndex />} />
          <Route path={paths.staff.event()} element={<StaffHome />} />
          <Route path={paths.staff.scan()} element={<StaffScan />} />
          <Route path={paths.staff.scanResult()} element={<StaffScanResult />} />
          <Route path={paths.staff.manual()} element={<StaffManual />} />
          <Route path={paths.staff.attendee()} element={<StaffAttendee />} />
          <Route path="*" element={<Navigate to={paths.staff.root} replace />} />
        </Route>
      </Route>

      <Route element={<RequireRole roles={ORGANISER_ROLES} />}>
        <Route path={paths.organiser.root} element={<OrganiserLayout />}>
          {sectionRoutes(ORGANISER_SECTIONS, { root: paths.organiser.root })}
          <Route path={paths.organiser.profile} element={<MyProfile />} />
          <Route path="*" element={<Navigate to={paths.organiser.root} replace />} />
        </Route>
      </Route>

      <Route element={<RequireRole roles={[ROLES.ADMIN]} />}>
        <Route path={paths.admin.root} element={<AdminLayout />}>
          <Route index element={<Navigate to={paths.admin.organisers} replace />} />
          {sectionRoutes(ADMIN_SECTIONS, { root: paths.admin.root })}
          <Route path={paths.admin.profile} element={<MyProfile />} />
          <Route path="*" element={<Navigate to={paths.admin.organisers} replace />} />
        </Route>
      </Route>
    </Routes>
  );
}
